package dev.kotlinfest._2026.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.JapaneseFontFamily
import dev.kotlinfest._2026.LatinFontFamily
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.layouts.PageLayoutData
import dev.kotlinfest._2026.components.widgets.SiteButton
import dev.kotlinfest._2026.desktopPageHorizontalPadding
import dev.kotlinfest._2026.sitePageHero
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private data class Ticket(
    val title: String,
    val description: String,
    val salesPeriod: String,
    val isSoldOut: Boolean = false,
    val price: String? = null,
    val tags: List<String> = emptyList(),
    val studentPrices: List<Pair<String, String>> = emptyList(),
)

private const val TicketPurchaseUrl = "https://conference.findy-code.io/conferences/kotlin-fest-2026/27/registration"

private val tickets = listOf(
    Ticket(
        title = "一般チケット（懇親会あり）",
        description = "懇親会付きの通常チケットです。セッション応募特典のクーポンコードをご利用の場合もこちらをご選択ください。",
        salesPeriod = "2026/08/24 ～ 2026/11/14",
        price = "¥9,000",
    ),
    Ticket(
        title = "一般チケット（懇親会なし）",
        description = "通常チケットです。セッション応募特典のクーポンコードをご利用の場合もこちらをご選択ください。",
        salesPeriod = "2026/08/24 ～ 2026/11/14",
        price = "¥7,000",
    ),
    Ticket(
        title = "学生チケット",
        description = "学生限定チケットです。受付時に学生証をご提示ください。数量限定ですのでお早めにお申し込みください。",
        salesPeriod = "2026/08/24 ～ 2026/11/14",
        tags = listOf("数量限定"),
        studentPrices = listOf("懇親会あり" to "¥1,500", "懇親会なし" to "¥0"),
    ),
    Ticket(
        title = "アーリーバードチケット（懇親会あり）",
        description = "販売は終了しました。多くのご購入ありがとうございました。",
        salesPeriod = "2026/08/24 ～ 2026/10/03",
        isSoldOut = true,
        price = "¥5,000",
        tags = listOf("期間限定"),
    ),
    Ticket(
        title = "アーリーバードチケット（懇親会なし）",
        description = "販売は終了しました。多くのご購入ありがとうございました。",
        salesPeriod = "2026/08/24 ～ 2026/10/03",
        isSoldOut = true,
        price = "¥4,000",
        tags = listOf("期間限定"),
    ),
)

val TicketHeroStyle = CssStyle {
    base { Modifier.sitePageHero(240).display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.Center) }
    Breakpoint.MD { Modifier.sitePageHero(380) }
}

val TicketHeroTitleStyle = CssStyle {
    base { Modifier.fontFamily(LatinFontFamily, "sans-serif").fontSize(32.px).fontWeight(FontWeight.SemiBold).lineHeight(1.2).letterSpacing(0.16.em) }
    Breakpoint.MD { Modifier.fontSize(48.px) }
}

val TicketHeroSubTitleStyle = CssStyle.base {
    Modifier.fontFamily(JapaneseFontFamily, "sans-serif").fontSize(12.px).fontWeight(FontWeight.Bold).letterSpacing(0.1.em)
}

val TicketContentStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().padding(top = 64.px, bottom = 80.px, leftRight = SiteLayout.MobileMarginPx.px).gap(64.px)
            .styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD {
        Modifier.maxWidth(SiteLayout.DesktopDesignWidthPx.px)
            .padding(top = 120.px, bottom = 160.px).desktopPageHorizontalPadding()
            .styleModifier {
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
    }
}

val TicketIntroStyle = CssStyle.base {
    Modifier.gap(24.px).fontSize(16.px).fontWeight(FontWeight.Bold).lineHeight(1.6).letterSpacing(0.1.em)
}

val TicketGridStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { size(1.fr) }.gap(16.px) }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } }.gap(20.px) }
}

val TicketCardStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().padding(24.px).gap(16.px).border(2.px, LineStyle.Solid, SitePalette.brand.pink)
            .borderRadius(20.px).backgroundColor(SitePalette.brand.white).styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD { Modifier.padding(32.px) }
}

val StudentTicketCardStyle = CssStyle {
    base { Modifier }
    Breakpoint.MD { Modifier.styleModifier { property("grid-column", "1 / -1") } }
}

val TicketTagStyle = CssStyle.base {
    Modifier.padding(topBottom = 4.px, leftRight = 8.px).borderRadius(8.px).backgroundColor(SitePalette.brand.softLime)
        .fontSize(14.px).fontWeight(500).lineHeight(1.6).letterSpacing(0.1.em)
}

val SoldOutTicketTagStyle = CssStyle.base {
    Modifier.backgroundColor(SitePalette.brand.border).color(SitePalette.brand.ink)
}

val TicketTitleStyle = CssStyle.base {
    Modifier.fontSize(22.px).fontWeight(FontWeight.Bold).lineHeight(1.6).letterSpacing(0.08.em).color(SitePalette.brand.pink)
}

val SoldOutTicketCardStyle = CssStyle.base {
    Modifier.color(SitePalette.brand.darkGray).styleModifier { property("border-color", SitePalette.brand.lightGray) }
}

val SoldOutTicketTitleStyle = CssStyle.base {
    Modifier.color(SitePalette.brand.darkGray)
}

val TicketDescriptionStyle = CssStyle.base {
    Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).lineHeight(1.6).letterSpacing(0.1.em)
}

val TicketDeadlineStyle = CssStyle.base {
    Modifier.fontSize(14.px).fontWeight(500).lineHeight(1.6).letterSpacing(0.1.em)
}

val TicketCardFooterStyle = CssStyle.base {
    Modifier.fillMaxWidth().display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .gap(16.px).styleModifier { property("flex-wrap", "wrap") }
}

val TicketPriceStyle = CssStyle.base {
    Modifier.fontFamily(LatinFontFamily, "sans-serif").fontSize(32.px).fontWeight(FontWeight.SemiBold).lineHeight(1.6).letterSpacing(0.08.em)
}

val StudentPriceLabelStyle = CssStyle.base {
    Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).lineHeight(1.6).letterSpacing(0.1.em)
}

val TicketTextSectionStyle = CssStyle.base { Modifier.fillMaxWidth().gap(16.px) }

val TicketSectionTitleStyle = CssStyle.base {
    Modifier.fontSize(26.px).fontWeight(FontWeight.Bold).lineHeight(2).letterSpacing(0.08.em)
}

val TicketLegalTextStyle = CssStyle.base {
    Modifier.fontSize(14.px).fontWeight(500).lineHeight(1.6).letterSpacing(0.1.em)
}

@Composable
private fun TicketTags(tags: List<String>, isSoldOut: Boolean = false) {
    if (tags.isNotEmpty()) {
        Row(Modifier.gap(8.px)) {
            tags.forEach {
                SpanText(it, TicketTagStyle.toModifier().then(if (isSoldOut) SoldOutTicketTagStyle.toModifier() else Modifier))
            }
        }
    }
}

@Composable
private fun TicketCard(ticket: Ticket, isStudent: Boolean = false) {
    Column(
        TicketCardStyle.toModifier()
            .then(if (isStudent) StudentTicketCardStyle.toModifier() else Modifier)
            .then(if (ticket.isSoldOut) SoldOutTicketCardStyle.toModifier() else Modifier),
    ) {
        TicketTags(if (ticket.isSoldOut) listOf("販売終了") else ticket.tags, ticket.isSoldOut)
        SpanText(ticket.title, TicketTitleStyle.toModifier().then(if (ticket.isSoldOut) SoldOutTicketTitleStyle.toModifier() else Modifier))
        SpanText(ticket.description, TicketDescriptionStyle.toModifier())
        SpanText("販売期間: ${ticket.salesPeriod}", TicketDeadlineStyle.toModifier())
        Row(TicketCardFooterStyle.toModifier()) {
            if (ticket.studentPrices.isEmpty()) {
                SpanText(ticket.price.orEmpty(), TicketPriceStyle.toModifier())
            } else {
                Column {
                    ticket.studentPrices.forEach { (label, price) ->
                        Row(Modifier.gap(16.px).alignItems(AlignItems.Center)) {
                            SpanText(label, StudentPriceLabelStyle.toModifier())
                            SpanText(price, TicketPriceStyle.toModifier())
                        }
                    }
                }
            }
            if (ticket.isSoldOut) {
                SiteButton("販売終了", enabled = false)
            } else {
                SiteButton("チケット購入", href = TicketPurchaseUrl)
            }
        }
    }
}

@Composable
private fun Notes() {
    Column(TicketTextSectionStyle.toModifier()) {
        SpanText("注意事項", TicketSectionTitleStyle.toModifier())
        Ul(attrs = {
            style {
                margin(0.px)
                paddingLeft(24.px)
            }
        }) {
            Li { Text("Kotlin Fest 2026はオフラインチケットのみの販売となり、当日のオンライン配信は予定しておりません。") }
            Li { Text("原則いかなる理由でもご返金はいたしかねます。") }
            Li {
                Text("Kotlin Festに参加するすべての人はこちらの")
                Link("/code-of-conduct", "行動規範")
                Text("を守ることを求められます。")
            }
            Li {
                Text("Kotlin Festの")
                Link("/privacy-policy", "プライバシーポリシー")
                Text("にご同意ください。")
            }
            Li {
                Text("チケットの購入が完了後、ご登録のメールアドレスに自動で領収書が送信されます。また、一般社団法人日本Kotlinユーザーグループは、適格請求書発行事業者登録を行っていないため、適格請求書発行事業者番号が掲載されている請求書の発行はできません。予めご了承ください。")
            }
        }
    }
}

@Composable
private fun LegalNotice() {
    Column(TicketTextSectionStyle.toModifier()) {
        SpanText("特定商取引法に基づく表記", TicketSectionTitleStyle.toModifier())
        Column(TicketLegalTextStyle.toModifier()) {
            SpanText("販売業者: 一般社団法人日本Kotlinユーザーグループ")
            SpanText("運営責任者: 日高正博 @mhidaka")
            SpanText("所在地: 東京都台東区北上野2丁目8-4コンフォート上野501号 050-5275-3602")
            Row(Modifier.styleModifier { property("flex-wrap", "wrap") }) {
                SpanText("連絡方法: 問い合わせ窓口：電子メール ")
                A(href = "mailto:contact@kotlinfest.dev") { Text("contact@kotlinfest.dev") }
            }
            SpanText("販売商品と価格: イベントページに記載された商品・価格に準じます")
            SpanText("商品代金以外の付帯費用: なし")
            SpanText("支払方法: Peatixの支払い方法に準じます")
            SpanText("商品の発送: 決済手続き後、メール送信を行うことでチケット引渡が完了します")
            SpanText("キャンセルについて: 万が一キャンセルされた場合でもチケット代金の返金はいたしかねます")
        }
    }
}

@InitRoute
fun initTicketPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Ticket", fullWidth = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun TicketPage() {
    Column(Modifier.fillMaxWidth()) {
        Box(TicketHeroStyle.toModifier(), contentAlignment = Alignment.Center) {
            Column(Modifier.gap(8.px), horizontalAlignment = Alignment.CenterHorizontally) {
                SpanText("Ticket", TicketHeroTitleStyle.toModifier())
                SpanText("チケット", TicketHeroSubTitleStyle.toModifier())
            }
        }
        Column(TicketContentStyle.toModifier()) {
            Column(TicketIntroStyle.toModifier()) {
                SpanText("Kotlin Fest 2026のチケットは5種類ご用意しており、一部数量に限りがございます。\n事前に注意事項をよくお読みの上ご購入お願いします。")
                SpanText("※ スポンサー様、登壇者様は別途専用ページを共有しますので、そちらから登録してください。")
            }
            Column(Modifier.fillMaxWidth().gap(64.px)) {
                Column(TicketGridStyle.toModifier()) {
                    tickets.forEach { ticket -> TicketCard(ticket, isStudent = ticket.studentPrices.isNotEmpty()) }
                }
                Notes()
                LegalNotice()
            }
        }
    }
}
